import '@fontsource/roboto';
import { LocalizationProvider } from '@mui/lab';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import { LicenseInfo } from '@mui/x-data-grid-premium';
import {
    BackendServiceType,
    getCustomBrowserAuthClients,
} from '@price-for-profit/micro-services';
import React from 'react';
import ReactDOM from 'react-dom';
import { BrowserRouter } from 'react-router-dom';
import {
    ProvideAuth,
    ProvideErrorBoundary,
    ProvideQueryClient,
    ProvideService,
    ProvideSnackbar,
    ProvideTheme,
} from 'shared/providers';
import { ProvideHttp } from 'shared/providers/provide-http';
import { CustomBackendAppConfg, NoServiceType } from 'shared/types';
import { getAppConfig } from 'startup/get-app-config';
import { registerGoogleAnalytics } from 'startup/register-google-analytics';
import App from './App';
import './index.css';
import { createServiceContext } from 'startup';

const run = async () => {
    const baseUrl = process.env.PUBLIC_URL;
    const appConfig = (await getAppConfig(baseUrl)) as CustomBackendAppConfg;
    const { clientApp, backendHttpClient, httpClient } = await getCustomBrowserAuthClients<
        NoServiceType,
        BackendServiceType
    >({
        clientId: appConfig.appId,
        authDomainHost: appConfig.authDomainHost,
        authorityUrl: appConfig.authorityUrl,
        cacheLocation: 'sessionStorage',
        serviceBaseUrl: appConfig.serviceBaseUrl,
        clientScopes: {
            event: appConfig.eventId,
            export: appConfig.exportAppId,
            auth: appConfig.authorizationAppId,
            pbi: appConfig.powerBiAppId,
            lambda: appConfig.lambdaAppId,
            das: appConfig.dataAccessAppId
        },
        backendScopes: {
            paragonbackend: appConfig.serviceId,
        },
    });

    const MUI_LICENSE_KEY =
        '167b9766858601f1feb4733002e25574Tz0xMjkwNzEsRT0xODA5OTA3MTk5MDAwLFM9cHJlbWl1bSxMTT1hbm51YWwsUFY9aW5pdGlhbCxLVj0y';

    LicenseInfo.setLicenseKey(MUI_LICENSE_KEY);

    let environment = 'prod';
    if (appConfig.serviceBaseUrl.includes('modeling') || appConfig.serviceBaseUrl.includes('dev')) {
        environment = 'dev';
    } else if (appConfig.serviceBaseUrl.includes('sandbox')) {
        environment = 'sandbox';
    }

    const serviceContext = createServiceContext({
        appConfig,
        httpClient,
        backendHttpClient,
        environment,
    });

    if (appConfig.enableGoogleAnalytics) {
        registerGoogleAnalytics(appConfig.googleTagId);
    }

    ReactDOM.render(
        <React.StrictMode>
            <BrowserRouter basename={baseUrl}>
                <ProvideTheme>
                    <ProvideSnackbar>
                        <ProvideErrorBoundary>
                            <ProvideService context={serviceContext}>
                                <ProvideAuth clientApp={clientApp}>
                                    <ProvideHttp context={backendHttpClient}>
                                        <ProvideQueryClient>
                                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                <App />
                                            </LocalizationProvider>
                                        </ProvideQueryClient>
                                    </ProvideHttp>
                                </ProvideAuth>
                            </ProvideService>
                        </ProvideErrorBoundary>
                    </ProvideSnackbar>
                </ProvideTheme>
            </BrowserRouter>
        </React.StrictMode>,
        document.getElementById('root')
    );
};

run().catch(console.error);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
//reportWebVitals();
